import { LitElement, html, css } from 'lit';

/**
 * crusher-stat, data metric tile.
 *
 * Existing usage (unchanged):
 *   <crusher-stat label="Active users" value="12,438"></crusher-stat>
 *   <crusher-stat label="Shipping velocity" value="12 merges" delta="+18%" meta="This week" tone="success"></crusher-stat>
 *
 * KPI hero with trend slot:
 *   <crusher-stat size="xl" label="Monthly recurring revenue" value="$42,800">
 *     <span slot="icon"><svg ...></svg></span>
 *     <span slot="trend" data-direction="up">+12.4% vs last month</span>
 *   </crusher-stat>
 *
 * Horizontal inline metric:
 *   <crusher-stat orientation="horizontal" label="Uptime" value="99.97%"></crusher-stat>
 *
 * Attributes:
 *   label        {String} , metric label (eyebrow text)
 *   value        {String} , primary display value
 *   meta         {String} , secondary descriptor below the value
 *   delta        {String} , simple badge string (use trend slot for color-coded direction)
 *   tone         {String} , accent color: brand | neutral | info | success | warning | danger (default: brand)
 *   size         {String} , sm | md | lg | xl (default: md). xl = KPI hero treatment.
 *   orientation  {String} , vertical | horizontal (default: vertical). horizontal = label+value on one row.
 *
 * Slots:
 *   (default), arbitrary footer content
 *   icon: leading icon shown to the left of the label/value group
 *   trend: delta indicator with optional data-direction="up|down" for color coding
 *
 * Notes:
 *   - The `delta` attribute continues to work for simple badge strings.
 *   - If both `delta` attribute and `trend` slot are present, trend slot wins
 *     and a one-time console.warn is emitted.
 */
export class CrusherStat extends LitElement {
  static properties = {
    label:       { type: String },
    value:       { type: String },
    meta:        { type: String },
    delta:       { type: String },
    tone:        { type: String, reflect: true },
    size:        { type: String, reflect: true },
    orientation: { type: String, reflect: true },
  };

  #warnedDeltaConflict = false;

  constructor() {
    super();
    this.label       = '';
    this.value       = '';
    this.meta        = '';
    this.delta       = '';
    this.tone        = 'brand';
    this.size        = 'md';
    this.orientation = 'vertical';
  }

  static styles = css`
    /* ------------------------------------------------------------------ */
    /* Tone accent                                                          */
    /* ------------------------------------------------------------------ */
    :host {
      --_stat-accent: var(--crusher-stat-tone-brand, var(--crusher-color-brand-primary));
      display: block;
    }

    :host([tone="neutral"]) { --_stat-accent: var(--crusher-stat-tone-neutral, var(--crusher-fg-muted)); }
    :host([tone="brand"])   { --_stat-accent: var(--crusher-stat-tone-brand, var(--crusher-color-brand-primary)); }
    :host([tone="info"])    { --_stat-accent: var(--crusher-stat-tone-info, var(--crusher-feedback-info)); }
    :host([tone="success"]) { --_stat-accent: var(--crusher-stat-tone-success, var(--crusher-feedback-success)); }
    :host([tone="warning"]) { --_stat-accent: var(--crusher-stat-tone-warning, var(--crusher-feedback-warning)); }
    :host([tone="danger"])  { --_stat-accent: var(--crusher-stat-tone-danger, var(--crusher-feedback-danger)); }

    /* ------------------------------------------------------------------ */
    /* Size scale, CSS variable overrides; md = current defaults          */
    /* ------------------------------------------------------------------ */
    :host([size="sm"]) {
      --_value-size:  var(--crusher-font-size-xl);
      --_label-size:  var(--crusher-font-size-xs);
      --_gap:         var(--crusher-spacing-2);
      --_padding-b:   var(--crusher-spacing-2);
      --_padding-i:   var(--crusher-spacing-3);
      --_padding-is:  calc(var(--crusher-spacing-3) + var(--crusher-spacing-2));
    }

    :host([size="md"]),
    :host(:not([size])) {
      --_value-size:  var(--crusher-stat-value-size, var(--crusher-font-size-4xl));
      --_label-size:  var(--crusher-font-size-xs);
      --_gap:         var(--crusher-layout-gap-tight, var(--crusher-spacing-4));
      --_padding-b:   var(--crusher-stat-padding-block, var(--crusher-spacing-4));
      --_padding-i:   var(--crusher-stat-padding-inline-end, var(--crusher-spacing-4));
      --_padding-is:  var(--crusher-stat-padding-inline-start, calc(var(--crusher-spacing-4) + var(--crusher-spacing-2)));
    }

    :host([size="lg"]) {
      --_value-size:  var(--crusher-font-size-5xl);
      --_label-size:  var(--crusher-font-size-sm);
      --_gap:         var(--crusher-spacing-5);
      --_padding-b:   var(--crusher-spacing-5);
      --_padding-i:   var(--crusher-spacing-5);
      --_padding-is:  calc(var(--crusher-spacing-5) + var(--crusher-spacing-2));
    }

    :host([size="xl"]) {
      --_value-size:  clamp(var(--crusher-font-size-5xl), 5vw, 4rem);
      --_label-size:  var(--crusher-font-size-base);
      --_gap:         var(--crusher-spacing-6);
      --_padding-b:   var(--crusher-spacing-6);
      --_padding-i:   var(--crusher-spacing-6);
      --_padding-is:  calc(var(--crusher-spacing-6) + var(--crusher-spacing-2));
    }

    /* ------------------------------------------------------------------ */
    /* Base card                                                            */
    /* ------------------------------------------------------------------ */
    .stat {
      background: var(--crusher-stat-bg, var(--crusher-surface));
      border: var(--crusher-component-border-weight) solid var(--crusher-stat-border, var(--crusher-border));
      border-radius: var(--crusher-radius-lg);
      box-shadow: var(--crusher-shadow-1);
      display: grid;
      gap: var(--_gap, var(--crusher-layout-gap-tight, var(--crusher-spacing-4)));
      min-inline-size: 0;
      overflow: hidden;
      padding-block: var(--_padding-b, var(--crusher-stat-padding-block, var(--crusher-spacing-4)));
      padding-inline-end: var(--_padding-i, var(--crusher-stat-padding-inline-end, var(--crusher-spacing-4)));
      padding-inline-start: var(--_padding-is, var(--crusher-stat-padding-inline-start, calc(var(--crusher-spacing-4) + var(--crusher-spacing-2))));
      position: relative;
    }

    /* Left accent stripe */
    .stat::before {
      background: linear-gradient(180deg, color-mix(in srgb, var(--_stat-accent) 90%, transparent), color-mix(in srgb, var(--_stat-accent) 35%, transparent));
      content: '';
      inline-size: calc(var(--crusher-spacing-1) + var(--crusher-component-border-weight));
      inset-block: 0;
      inset-inline-start: 0;
      position: absolute;
    }

    /* ------------------------------------------------------------------ */
    /* Orientation: horizontal, label left, value right on one row        */
    /* ------------------------------------------------------------------ */
    :host([orientation="horizontal"]) .stat {
      align-items: center;
      gap: var(--crusher-spacing-4);
      grid-template-columns: 1fr auto;
      grid-template-rows: auto;
    }

    :host([orientation="horizontal"]) .eyebrow {
      /* occupies column 1; value occupies column 2 */
      grid-column: 1;
      grid-row: 1;
    }

    :host([orientation="horizontal"]) .value {
      grid-column: 2;
      grid-row: 1;
      white-space: nowrap;
    }

    :host([orientation="horizontal"]) .meta {
      grid-column: 1 / -1;
      grid-row: 2;
    }

    :host([orientation="horizontal"]) .footer {
      grid-column: 1 / -1;
    }

    :host([orientation="horizontal"]) .trend-area {
      grid-column: 1 / -1;
    }

    /* ------------------------------------------------------------------ */
    /* Icon slot, shifts layout to make space on the left                 */
    /* ------------------------------------------------------------------ */
    .stat.has-icon {
      grid-template-columns: auto 1fr;
    }

    .stat.has-icon::before {
      /* Suppress the accent stripe when an icon slot occupies the left edge */
      display: none;
    }

    .icon-slot-wrapper {
      align-self: center;
      color: var(--_stat-accent);
      display: flex;
      grid-column: 1;
      grid-row: 1 / -1;
    }

    .stat.has-icon .eyebrow,
    .stat.has-icon .value,
    .stat.has-icon .meta,
    .stat.has-icon .footer,
    .stat.has-icon .trend-area {
      grid-column: 2;
    }

    /* ------------------------------------------------------------------ */
    /* Eyebrow row                                                          */
    /* ------------------------------------------------------------------ */
    .eyebrow {
      align-items: center;
      display: flex;
      flex-wrap: wrap;
      gap: var(--crusher-spacing-2);
      justify-content: space-between;
      min-inline-size: 0;
    }

    .label {
      color: var(--crusher-fg-muted);
      font-size: var(--_label-size, var(--crusher-font-size-xs));
      font-weight: var(--crusher-font-weight-semibold);
      letter-spacing: 0.08em;
      margin: 0;
      text-transform: uppercase;
      /* Allow the label to shrink inside a narrow host (theme matrix cell,
         dashboard tile) instead of pushing the delta past the edge. */
      min-inline-size: 0;
      max-inline-size: 100%;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .delta {
      background: color-mix(in srgb, var(--_stat-accent) 14%, transparent);
      border-radius: var(--crusher-radius-full);
      color: var(--_stat-accent);
      font-size: var(--crusher-font-size-xs);
      font-weight: var(--crusher-font-weight-semibold);
      padding-inline: var(--crusher-spacing-2);
      padding-block: calc(var(--crusher-spacing-1) / 2);
      white-space: nowrap;
      flex: 0 0 auto;
    }

    /* ------------------------------------------------------------------ */
    /* Value                                                                */
    /* ------------------------------------------------------------------ */
    .value {
      color: var(--crusher-fg);
      font-size: var(--_value-size, var(--crusher-stat-value-size, var(--crusher-font-size-4xl)));
      font-weight: var(--crusher-font-weight-bold);
      line-height: 1;
      margin: 0;
      text-wrap: balance;
    }

    /* ------------------------------------------------------------------ */
    /* Meta                                                                 */
    /* ------------------------------------------------------------------ */
    .meta {
      color: var(--crusher-fg-muted);
      font-size: var(--crusher-font-size-sm);
      margin: 0;
    }

    /* ------------------------------------------------------------------ */
    /* Footer (default slot)                                               */
    /* ------------------------------------------------------------------ */
    .footer {
      min-inline-size: 0;
    }

    /* ------------------------------------------------------------------ */
    /* Trend slot, color-coded by data-direction                          */
    /* ------------------------------------------------------------------ */
    .trend-area {
      font-size: var(--crusher-font-size-sm);
      min-inline-size: 0;
    }

    ::slotted([slot="trend"]) {
      color: var(--crusher-text-secondary, var(--crusher-fg-muted));
      font-size: var(--crusher-font-size-sm);
      font-weight: var(--crusher-font-weight-semibold);
    }

    ::slotted([slot="trend"][data-direction="up"]) {
      color: var(--crusher-color-status-success);
    }

    ::slotted([slot="trend"][data-direction="down"]) {
      color: var(--crusher-color-status-failure);
    }
  `;

  /** Track whether the icon slot has content so we can toggle has-icon class. */
  #iconSlotHasContent = false;
  /** Track whether the trend slot has content so we know which source wins. */
  #trendSlotHasContent = false;

  #onIconSlotChange(e) {
    const slot = e.target;
    this.#iconSlotHasContent = slot.assignedNodes({ flatten: true }).length > 0;
    this.requestUpdate();
  }

  #onTrendSlotChange(e) {
    const slot = e.target;
    this.#trendSlotHasContent = slot.assignedNodes({ flatten: true }).length > 0;
    if (this.#trendSlotHasContent && this.delta && !this.#warnedDeltaConflict) {
      this.#warnedDeltaConflict = true;
      console.warn('[crusher-stat] Both `delta` attribute and `trend` slot are set. The trend slot takes precedence. Remove the `delta` attribute to silence this warning.');
    }
    this.requestUpdate();
  }

  render() {
    const showDelta = this.delta && !this.#trendSlotHasContent;

    return html`
      <div class="stat ${this.#iconSlotHasContent ? 'has-icon' : ''}" part="stat">
        ${this.#iconSlotHasContent ? html`
          <div class="icon-slot-wrapper" part="icon">
            <slot name="icon" @slotchange=${this.#onIconSlotChange}></slot>
          </div>
        ` : html`<slot name="icon" @slotchange=${this.#onIconSlotChange} style="display:none"></slot>`}

        <div class="eyebrow">
          <p class="label">${this.label}</p>
          ${showDelta ? html`<span class="delta">${this.delta}</span>` : null}
        </div>

        <p class="value">${this.value}</p>

        ${this.meta ? html`<p class="meta">${this.meta}</p>` : null}

        <div class="trend-area" part="trend">
          <slot name="trend" @slotchange=${this.#onTrendSlotChange}></slot>
        </div>

        <div class="footer" part="footer">
          <slot></slot>
        </div>
      </div>
    `;
  }
}

customElements.define('crusher-stat', CrusherStat);
